import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Flip } from "react-toastify";
import api from "../axiosConfig";
import { SignupFormData } from "../types/authTypes";
import { mapApiErrorToField } from "../utils/errorMapper";

interface UseSignupHandlerProps {
  formData: SignupFormData;
  setError: (field: keyof SignupFormData, message: string) => void;
  clearError: (field: keyof SignupFormData) => void;
}

export const useSignupHandler = ({ formData, setError, clearError }: UseSignupHandlerProps) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isFormValid = useMemo(() => Boolean(formData.user_name && formData.organization_name && formData.email_id && formData.business_type), [formData]);

  const handleRegister = async () => {
    if (!isFormValid) {
      toast.error("Please fill in all required fields", { theme: "colored" });
      return;
    }

    setLoading(true);
    try {
      await api.post("/pos/register", formData);

      toast.success("Account created successfully", { transition: Flip });
      setTimeout(() => navigate("/"), 1000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Unknown error occurred";

      // 🔄 Reset possible field errors
      (["user_name", "organization_name", "email_id", "phone_number"] as const).forEach((field) => clearError(field));

      // 🚦 Map API errors automatically
      const mappedError = mapApiErrorToField(errorMessage);
      if (mappedError) {
        setError(mappedError.field, mappedError.message);
      } else {
        toast.error(errorMessage, { theme: "colored" });
      }
    } finally {
      setLoading(false);
    }
  };

  return { loading, isFormValid, handleRegister };
};
